import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import "./ProjectDetails.css";
import ChecklistPage from "./checkList";
import countryData from "./countryData.json";
import Select from "react-select";
import banner3 from "../NetZerobanner.png";
import { useParams } from "react-router-dom";
import { FaSignOutAlt } from "react-icons/fa";
import { BsAlarm } from "react-icons/bs";
import { useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { useAuthStore } from "../../store/useAuthStore";
import { useAutoSave } from "../../hooks/useAutoSave";
import {
  checkSitaCodeUnique as checkSitaCodeUniqueApi,
  getHotelByUserId,
  getHotelFullDetails,
  addHotelReadinessDetails,
} from "../../ApiService";

export const getAllCountries = () => Object.keys(countryData.countries).sort();

const ProjectDetails = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const { hotelId } = useParams();
  const [showPopup, setShowPopup] = useState(false);
  const [showSavedPopup, setShowSavedPopup] = React.useState(false);
  const [popupMessage, setPopupMessage] = React.useState("");
  const [originalUserId, setOriginalUserId] = useState(null);
  const location = useLocation();
  const isAdminEdit = location.state?._adminEdit === true;
  const isNewProjectFlag = location.state?.isNewProject || false;

  const {
    role,
    email,
    initializeAuth,
    logout,
    token: authToken,
  } = useAuthStore();
  const countryOptions = useMemo(
    () =>
      getAllCountries().map((country) => ({
        value: country,
        label: country,
      })),
    []
  );

  const contractTypeOptions = useMemo(
    () => [
      { value: "Leased", label: "Leased" },
      { value: "Managed", label: "Managed" },
      { value: "Franchised", label: "Franchised" },
    ],
    []
  );

  const handleLogout = () => {
    try {
      logout();
    } catch (err) {
      console.error("Error during logout:", err);
    }

    localStorage.removeItem("userRole");
    localStorage.removeItem("User");
    localStorage.setItem("logoutEvent", Date.now());
    navigate("/");
  };

  const [formData, setFormData] = useState({
    hotelName: "",
    sitaCode: "",
    country: "",
    contractType: "",
    managerName: "",
    managerEmail: "",
    jobTitle: "",
  });

  const [errors, setErrors] = useState({});
  const [checklistData, setChecklistData] = useState(null);

  useEffect(() => {
    if (formData.sitaCode && formData.sitaCode.length === 5) {
      checkSitaCodeUnique(formData.sitaCode, formData.hotelId);
    }
  }, [formData.sitaCode, formData.hotelId]);

  const checkSitaCodeUnique = async (
    code,
    hotelId = null,
    showToast = false
  ) => {
    try {
      const data = await checkSitaCodeUniqueApi(code, hotelId);

      if (!data.isUnique) {
        if (showToast) toast.error("SITA Code already exists!");
        setErrors((prev) => ({
          ...prev,
          sitaCode: "SITA Code already exists",
        }));
        return false;
      } else {
        setErrors((prev) => ({ ...prev, sitaCode: "" }));
        return true;
      }
    } catch (err) {
      console.error("Error checking SITA code:", err);
      if (showToast) toast.error("Error checking SITA Code. Please try again.");
      return false;
    }
  };
  const draftKey = currentUserId ? `hotelDraft_${currentUserId}` : "hotelDraft";
  const checklistDraftKey = currentUserId
    ? `checklistDraft_${currentUserId}`
    : "checklistDraft";

  useEffect(() => {
    initializeAuth();
    const User = localStorage.getItem("User");
    if (User) setCurrentUserId(User);
  }, []);

  useEffect(() => {
    if (email) {
      setFormData((prev) => ({
        ...prev,
        managerEmail: email || "",
      }));
    }
  }, [email]);

  useEffect(() => {
    if (!currentUserId) return;
    const savedDraft = localStorage.getItem(draftKey);
    if (savedDraft) {
      try {
        const { formData: savedForm, checklistData: savedChecklist } = JSON.parse(savedDraft);
        if (savedForm) setFormData(savedForm);
        if (savedChecklist) setChecklistData(savedChecklist);
        setIsEditMode(false);
      } catch (err) {
        console.error("Error parsing draft:", err);
      }
    }
  }, [currentUserId]);

  useEffect(() => {
    if (hotelId) {
      fetchHotelById(hotelId);
    } else if (currentUserId) {
      checkExistingHotel();
    }
  }, [hotelId, currentUserId]);

  const checkExistingHotel = async () => {
    setIsLoading(true);
    try {
      const data = await getHotelByUserId(currentUserId);

      if (data && data.hotelId) {
        setIsEditMode(true);
        setOriginalUserId(data.userId);
        setShowPopup(false);
        setFormData({
          hotelId: data.hotelId,
          hotelName: data.hotelName || "",
          sitaCode: data.sitaCode || "",
          country: data.country || "",
          contractType: data.contractType || "",
          managerName: data.managerName || "",
          managerEmail: data.managerEmail || "",
          jobTitle: data.jobTitle || "",
        });

        setChecklistData({
          readinessResponses: data.readinessResponses || [],
          totalPoints: data.totalPoints || 0,
          totalAnswered: data.totalAnswered || 0,
          readinessLevels: data.readinessLevels || "Not Ready",
        });
      } else {
        setIsEditMode(false);
        setShowPopup(true);
      }
    } catch (error) {
      console.error("Error checking hotel record:", error);
      setShowPopup(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHotelById = async (id) => {
    setIsLoading(true);
    try {
      const data = await getHotelFullDetails(id);

      setIsEditMode(true);
      setOriginalUserId(data.userId);

      setFormData({
        hotelId: data.hotelId,
        hotelName: data.hotelName || "",
        sitaCode: data.sitaCode || "",
        country: data.country || "",
        contractType: data.contractType || "",
        managerName: data.managerName || "",
        managerEmail: data.managerEmail || "",
        jobTitle: data.jobTitle || "",
      });

      setChecklistData({
        readinessResponses: data.readinessResponses || [],
        totalPoints: data.totalPoints || 0,
        totalAnswered: data.totalAnswered || 0,
        readinessLevels: data.readinessLevels || "Not Ready",
      });
    } catch (error) {
      console.error("Error fetching hotel by Id:", error);
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const saveDraft = (form, checklist) => {
    if (!draftKey) return;
    const draftData = {
      formData: form,
      checklistData: checklist,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(draftKey, JSON.stringify(draftData));
  };
  useAutoSave({ formData, checklistData }, (data) => {
    console.log("Draft SAVE attempt. isAdminEdit = ", isAdminEdit);
     if (isAdminEdit) return; 
     console.log("🚨 Draft SAVED - should NOT happen in edit mode"); 
    saveDraft(data.formData, data.checklistData);
  });
  console.log("FLAG RECEIVED:", location.state);
console.log("isAdminEdit:", isAdminEdit);

  const handleChecklistChange = useCallback((data) => {
    setChecklistData(data);
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.hotelName.trim())
      newErrors.hotelName = "Hotel Name is required";
    if (!formData.sitaCode.trim()) newErrors.sitaCode = "SITA Code is required";
    if (!formData.country.trim()) newErrors.country = "Country is required";
    if (!formData.contractType.trim())
      newErrors.contractType = "Contract Type is required";
    if (!formData.jobTitle.trim()) newErrors.jobTitle = "Job Title is required";
    if (!formData.managerName.trim())
      newErrors.managerName = "Manager Name is required";
    if (!formData.managerEmail.trim())
      newErrors.managerEmail = "Manager Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.managerEmail))
      newErrors.managerEmail = "Enter a valid email address";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (checklistData) => {
    if (!validateForm()) return;

    const isUnique = await checkSitaCodeUnique(
      formData.sitaCode,
      formData.hotelId,
      true
    );
    if (!isUnique) return;

    try {
      let userIdToSend;

      if (isEditMode) {
        userIdToSend = originalUserId;
      } else {
        userIdToSend = currentUserId;
      }
      const apiData = {
        ...formData,
        totalPoints: checklistData.totalPoints,
        totalAnswered: checklistData.totalAnswered,
        readinessLevels: checklistData.readinessLevels,
        projectId: 1,
        usersId: userIdToSend,
        updatedByAdmin: role === "Admin" ? currentUserId : null,
        readinessResponses: checklistData.readinessResponses || [],
      };

      // NOTE: original code used `localStorage.getItem("token")` for API token.
      const token = localStorage.getItem("token");

      const result = await addHotelReadinessDetails(apiData, token);

      if (
        result &&
        (result.status === 200 ||
          result.success === true ||
          result.message?.toLowerCase().includes("submission received") ||
          (typeof result === "string" &&
            result.toLowerCase().includes("submission received")))
      ) {
        localStorage.removeItem(draftKey);
        localStorage.removeItem(checklistDraftKey);

        setPopupMessage("Details have been saved successfully.");
        setShowSavedPopup(true);
      } else {
        toast.error(
          result?.message || "Failed to save details. Please try again."
        );
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error(
        error.message || "Something went wrong while saving details!"
      );
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading hotel details...</p>
      </div>
    );
  }

  return (
    <div>
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h2>Welcome to RHG Net Zero Assessment</h2>
            <p>
              In 2025, RHG has achieved the pinnacle of sustainable stays with
              the launch of the industry’s first Verified Net Zero Hotels, in
              Manchester and Oslo. To become a Verified Net Zero Hotel (VNZ),
              Hotels must comply with certain requirements such as energy
              efficiency, no fossil fuels usage and sustainable operations. The
              starting point is to comply with the hotel energy emissions
              requirements, known as Scope 1 & 2 for which this assessment tool
              is key to understand your hotel readiness.
            </p>
            <p>
              Please fill out the questionnaire to get to know your hotel
              readiness level. The focus is on energy emissions and the tool
              also considers relevant elements of Scope 3, which relates with
              emissions from hotel operations. Note that a final score is only
              possible when all questions have been answered.
            </p>
            <p>
              For technical difficulties, reach us at{" "}
              <span className="email-highlight">
                responsiblebusiness@radissonhotels.com
              </span>
              .We expect many queries, so we will get back to you as soon as
              possible.
            </p>
            <div className="popup-timer-footer">
              <div className="timer-left">
                <BsAlarm className="timer-icon" />
                <span>20 minutes</span>
              </div>
              <button
                onClick={() => setShowPopup(false)}
                className="popup-close"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      {showSavedPopup && (
        <div className="save-popup-overlay">
          <div className="save-popup-box">
            <h3>Success 🎉</h3>
            <p>{popupMessage}</p>
            <button onClick={() => setShowSavedPopup(false)}>OK</button>
          </div>
        </div>
      )}
      <form className="project-details" onSubmit={(e) => e.preventDefault()}>
        <img src={banner3} alt="Raddison Logo" className="company-logo" />
        <div className="hotel-header">
          <h2 className="project-title">🏢 Hotel Details</h2>
          <span className="mandatory-text">
            <span className="mandatory-star">*</span>{" "}
            <b>Indicates mandatory information</b>
          </span>
        </div>

        <div className="form-row">
          {/* Hotel Name */}
          <div className="form-group">
            <label className="form-label">
              Hotel Name <span className="required">*</span>
            </label>
            <input
              type="text"
              name="hotelName"
              value={formData.hotelName}
              onChange={handleChange}
              className={`form-input ${errors.hotelName ? "input-error" : ""}`}
            />
            {errors.hotelName && (
              <span className="error-text">{errors.hotelName}</span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              SITA Code <span className="required">*</span>
            </label>
            <input
              type="text"
              name="sitaCode"
              value={formData.sitaCode}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                if (/^[A-Z0-9]{0,5}$/.test(value) || value === "") {
                  handleChange({
                    target: { name: "sitaCode", value },
                  });
                }
              }}
              className={`form-input ${errors.sitaCode ? "input-error" : ""}`}
            />
            {errors.sitaCode && (
              <span className="error-text">{errors.sitaCode}</span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              Country <span className="required">*</span>
            </label>
            <Select
              options={countryOptions}
              value={
                countryOptions.find((c) => c.value === formData.country) || null
              }
              onChange={(option) => {
                const newForm = { ...formData, country: option.value };
                setFormData(newForm);
                if (!isAdminEdit) {
  saveDraft(newForm, checklistData);
}
              }}
              placeholder="Select Country"
              className={`form-select ${errors.country ? "select-error" : ""}`}
            />
            {errors.country && (
              <span className="error-text">{errors.country}</span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              Contract Type <span className="required">*</span>
            </label>
            <Select
              options={contractTypeOptions}
              value={
                contractTypeOptions.find(
                  (c) => c.value === formData.contractType
                ) || null
              }
              onChange={(option) => {
                const newForm = { ...formData, contractType: option.value };
                setFormData(newForm);
                if (!isAdminEdit) {
  saveDraft(newForm, checklistData);
}
              }}
              placeholder="Choose Contract Type"
              className={`form-select ${errors.country ? "select-error" : ""}`}
            />
            {errors.contractType && (
              <span className="error-text">{errors.contractType}</span>
            )}
          </div>
        </div>

        <h3 className="project-title">
          <i className="fa fa-user-circle-o"></i> Contact Details
        </h3>

        <div className="form-row-3">
          {/* Manager Name */}
          <div className="form-group">
            <label className="form-label">
              Name <span className="required">*</span>
            </label>
            <input
              type="text"
              name="managerName"
              value={formData.managerName}
              onChange={handleChange}
              className={`form-input ${
                errors.managerName ? "input-error" : ""
              }`}
            />
            {errors.managerName && (
              <span className="error-text">{errors.managerName}</span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              Job Title <span className="required">*</span>
            </label>
            <input
              type="text"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleChange}
              maxLength={50}
              className={`form-input ${errors.jobTitle ? "input-error" : ""}`}
            />
            {errors.jobTitle && (
              <span className="error-text">{errors.jobTitle}</span>
            )}
          </div>

          {/* Manager Email */}
          <div className="form-group">
            <label className="form-label">
              RHG Email Address{" "}
              <span className="required" style={{ visibility: "hidden" }}>
                *
              </span>
            </label>
            <input
              type="email"
              name="managerEmail"
              value={formData.managerEmail}
              className={`form-input ${
                errors.managerEmail ? "input-error" : ""
              }`}
              disabled
            />
            {errors.managerEmail && (
              <span className="error-text">{errors.managerEmail}</span>
            )}
          </div>
        </div>
      </form>
      <ToastContainer />

      <div>
        <ChecklistPage
          onSave={handleSubmit}
          onChecklistChange={handleChecklistChange}
          initialData={checklistData}
          isEditMode={isEditMode}
          isAdminEdit={isAdminEdit}
          hotelDetails={{
            hotelName: formData.hotelName,
            sitaCode: formData.sitaCode,
            country: formData.country,
            contractType: formData.contractType,
          }}
        
          contactDetails={{
            name: formData.managerName,
            jobTitle: formData.jobTitle,
            email: formData.managerEmail,
          }}
        />
      </div>
      <button className="logout-btn" onClick={handleLogout}>
        <FaSignOutAlt className="logout-icon" /> Logout
      </button>
    </div>
  );
};

export default React.memo(ProjectDetails);
